import { AxiosInstance } from 'axios'
import { useMutation, useQueryClient } from 'react-query'
import { useAxiosContext } from '@dc-shared-web/providers/AxiosProvider'
import { useToast } from '@dc-shared-web/components/shadcn/use-toast'
import {
  DOCUMENTS_QUERY_KEY,
  LIST_QUERY_KEY,
} from '@dc-shared-web/hooks/api/queryKeys'
import {
  CrunchesPageEntities,
  shouldInvalidateCrunchesPage,
} from '@dc-shared-web/hooks/utils/shouldInvalidateCrunchesPage'

type ShareDocumentParams = {
  uuid: string
  name: string
  recipients: string
  message?: string
}

type ShareDocumentResponseItem = {
  email: string
  error?: string
}

export const shareDocument = async (
  apiClient: AxiosInstance,
  params: ShareDocumentParams
): Promise<ShareDocumentResponseItem[]> => {
  const { data } = await apiClient.postForm('/ajax/crunches/share', {
    uuid: params.uuid,
    email: params.recipients,
    message: params.message,
  })
  return data
}

export const useShareDocument = (params: {
  documentId: string
  onSuccess?: () => void
  onError?: () => void
}) => {
  const apiClient = useAxiosContext()
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation(
    (mutationParams: ShareDocumentParams) =>
      shareDocument(apiClient, mutationParams),
    {
      onSuccess: async (_, context) => {
        await queryClient.invalidateQueries<CrunchesPageEntities>({
          queryKey: [DOCUMENTS_QUERY_KEY, LIST_QUERY_KEY],
          refetchPage: (page) =>
            shouldInvalidateCrunchesPage(page, params.documentId),
        })

        toast({
          variant: 'success',
          title: `${context.name} has been shared.`,
        })
        params.onSuccess?.()
      },
      onError: () => {
        toast({
          variant: 'error',
          title: 'Error sharing document',
          description: 'If the issue persists, please contact support.',
        })
        params.onError?.()
      },
    }
  )
}
